/**
 * Projects query options factories
 *
 * Centralizes query configuration for project-related queries.
 * Used in route loaders for prefetching and in components via useQuery.
 */

import { queryOptions } from '@tanstack/react-query';
import type { ApiClient } from '@/lib/api';
import type { ProjectStatus } from '@/lib/api/types';

interface ProjectsListParams {
  page?: number;
  limit?: number;
  includeArchived?: boolean;
  /**
   * Explicit status allowlist (e.g. `['active', 'paused']`). Passed to the
   * API as CSV and, critically, folded into the queryKey below — this
   * factory is shared by 7 call sites (ProjectMultiSelect, PresetGalleryTab,
   * UploadRecordModal, CreateRuleModal, projects.tsx, -projects.lazy.tsx,
   * useSettingsProjects.ts), and without it in the key they'd all share one
   * cache slot: whichever mounted first would decide, for everyone else,
   * whether archived projects show up.
   */
  statuses?: ProjectStatus[];
}

export function projectsListOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null,
  params: ProjectsListParams = {}
) {
  const { page = 1, limit = 20, includeArchived = true, statuses } = params;
  const offset = (page - 1) * limit;

  return queryOptions({
    queryKey: [
      'projects',
      'list',
      selectedOrgId,
      { page, limit, includeArchived, statuses },
    ] as const,
    queryFn: () =>
      apiClient.projects.getProjects({
        includeArchived: String(includeArchived),
        limit: String(limit),
        offset: String(offset),
        ...(statuses && statuses.length > 0 ? { statuses: statuses.join(',') } : {}),
      }),
    enabled: !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * The endpoint's own page ceiling (`GET /projects`, `ProjectsQuerySchema.limit`
 * max 100 in edge-api `routes/projects/core.ts`; the api-types list schema says
 * 1000, the route refuses anything over 100). Asking for more is a 400, so a
 * reader of one page owes its user a truncation notice above this.
 */
export const LIVE_PROJECTS_PAGE_LIMIT = 100;

/**
 * Every live (active + paused) project on one maximum page — the setup map's
 * report-language card and the script-scoring coverage read this same query,
 * so they share one cache entry instead of two hand-matched parameter sets.
 */
export function liveProjectsListOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null
) {
  return projectsListOptions(apiClient, selectedOrgId, {
    limit: LIVE_PROJECTS_PAGE_LIMIT,
    includeArchived: false,
    statuses: ['active', 'paused'],
  });
}

export function projectDetailOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null,
  projectId: string
) {
  return queryOptions({
    queryKey: ['projects', 'detail', selectedOrgId, projectId] as const,
    queryFn: () => apiClient.projects.getProjectById(projectId),
    enabled: !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
  });
}
